import { ref, watch } from "vue";
import KeenSlider, { type KeenSliderInstance } from "keen-slider";
import { type DsButtonRefType } from "@sazka/design-system/src/button/ds-button-reference";
import { useBreakpoints } from "@sazka/utils/composables/use-breakpoints";
import { onViewportEnter } from "@sazka/utils/viewport-enter";
import useCustomNavigationPlugin from "@/shared/keen-slider/plugins/keen-custom-navigation-plugin";

const cardsInRow: Record<string, number> = {
    xs: 3,
    xs2: 4,
    sm: 6,
    sm2: 8,
    lg: 10,
    xl: 12,
};

const isSm = useBreakpoints("sm");

const initCarousel = async (carouselWrapper: HTMLElement) => {
    const shouldInit = ref(false);
    let carousel: KeenSliderInstance = null;

    const carouselElement = ref<HTMLElement>(carouselWrapper.querySelector(".games-cards-section"));
    if (!carouselElement.value) return;

    const prevButton = ref<DsButtonRefType>(
        carouselWrapper.querySelector<HTMLButtonElement>(".js-games-carousel-prev")
    );
    const dots = ref<HTMLElement>(carouselWrapper.querySelector(".js-games-carousel-dots"));
    const nextButton = ref<DsButtonRefType>(
        carouselWrapper.querySelector<HTMLButtonElement>(".js-games-carousel-next")
    );
    const doubleCards = carouselElement.value.classList.contains("games-cards-section--double") ? 2 : 1;

    await onViewportEnter(carouselElement.value, 0, "400px").then(() => (shouldInit.value = true));
    const isIos = document.body.classList.contains("ios");

    watch(
        isSm,
        (newVal) => {
            if (newVal && shouldInit.value && !isIos) {
                carouselElement.value.style.gap = "0";
                carouselElement.value.classList.remove("games-cards-section--mobile");
                carousel = new KeenSlider(
                    carouselElement.value,
                    {
                        loop: false,
                        mode: "free-snap",
                        slides: {
                            perView: doubleCards == 2 ? 1.5 : 3,
                            spacing: 10,
                        },
                        defaultAnimation: {
                            duration: 1000,
                        },
                        selector: ".games-cards-section-carousel__item",
                        breakpoints: {
                            "(min-width: 480px)": {
                                slides: { perView: cardsInRow["xs2"] / doubleCards, spacing: 10 },
                            },
                            "(min-width: 768px)": {
                                slides: { perView: cardsInRow["sm"] / doubleCards, spacing: 10 },
                            },
                            "(min-width: 1024px)": {
                                slides: { perView: cardsInRow["sm2"] / doubleCards, spacing: 10 },
                            },
                            "(min-width: 1680px)": {
                                slides: { perView: cardsInRow["lg"] / doubleCards, spacing: 10 },
                            },
                            "(min-width: 2200px)": {
                                slides: { perView: cardsInRow["xl"] / doubleCards, spacing: 10 },
                            },
                        },
                    },
                    [useCustomNavigationPlugin(prevButton, nextButton, dots, true)]
                );
            } else {
                carouselElement.value.removeAttribute("style");
                carouselElement.value.classList.add("games-cards-section--mobile");
                if (carousel) carousel.destroy();
            }
        },
        {
            immediate: true,
        }
    );

    return carousel;
};

const useCardsGridCarousel = () => {
    const carouselWrappers = [...document.querySelectorAll<HTMLElement>(".games-cards-section-carousel")];
    if (environment.isEditMode) return;

    for (const carousel of carouselWrappers) {
        initCarousel(carousel);
    }
};

export { useCardsGridCarousel, initCarousel };
